const STORAGE_PREFIX = 'carthagoPopup';

function isStandardPopup(popup) {
  return popup?.classList.contains('carthago-popup--standard');
}

function getPopupKey(popup) {
  return `${STORAGE_PREFIX}:${popup.dataset.popupId || 'unknown'}:${popup.dataset.popupVersion || '0'}`;
}

function shouldSkipByFrequency(popup) {
  const frequency = popup.dataset.popupFrequency || 'session';
  const key = getPopupKey(popup);

  if (frequency === 'always') {
    return false;
  }

  if (frequency === 'session') {
    try {
      return window.sessionStorage.getItem(key) === '1';
    } catch (_) {
      return false;
    }
  }

  if (frequency === 'days') {
    try {
      const stored = parseInt(window.localStorage.getItem(key), 10);
      const days = Math.max(1, parseInt(popup.dataset.popupFrequencyDays, 10) || 1);
      return stored > 0 && Date.now() - stored < days * 24 * 60 * 60 * 1000;
    } catch (_) {
      return false;
    }
  }

  return false;
}

function markAsSeen(popup) {
  const frequency = popup.dataset.popupFrequency || 'session';
  const key = getPopupKey(popup);

  try {
    if (frequency === 'session') {
      window.sessionStorage.setItem(key, '1');
    }

    if (frequency === 'days') {
      window.localStorage.setItem(key, String(Date.now()));
    }
  } catch (_) {}
}

function markAsSeenOnStandardInteraction(popup) {
  if (!isStandardPopup(popup)) {
    return;
  }

  markAsSeen(popup);
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((element) => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true');
}

function lockBodyScroll() {
  const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
  document.body.dataset.popupScrollLocked = '1';
  document.body.dataset.popupPreviousOverflow = document.body.style.overflow || '';
  document.body.dataset.popupPreviousPaddingRight = document.body.style.paddingRight || '';
  document.body.style.overflow = 'hidden';

  if (scrollBarWidth > 0) {
    document.body.style.paddingRight = `${scrollBarWidth}px`;
  }
}

function unlockBodyScroll() {
  if (document.querySelector('[data-carthago-popup].is-open')) {
    return;
  }

  document.body.style.overflow = document.body.dataset.popupPreviousOverflow || '';
  document.body.style.paddingRight = document.body.dataset.popupPreviousPaddingRight || '';
  delete document.body.dataset.popupScrollLocked;
  delete document.body.dataset.popupPreviousOverflow;
  delete document.body.dataset.popupPreviousPaddingRight;
}

function updateCloseAvailability(popup) {
  const closeButton = popup.querySelector('[data-popup-close]');
  const content = popup.querySelector('[data-popup-content]');

  if (!closeButton || !content) {
    return;
  }

  if (popup.dataset.popupRequireScroll !== '1') {
    closeButton.disabled = false;
    closeButton.removeAttribute('aria-disabled');
    return;
  }

  const isAtEnd = content.scrollHeight <= content.clientHeight + 2 || content.scrollTop + content.clientHeight >= content.scrollHeight - 2;
  closeButton.disabled = !isAtEnd;
  closeButton.setAttribute('aria-disabled', isAtEnd ? 'false' : 'true');
}

function openPopup(popup) {
  if (!popup || popup.dataset.popupOpened === '1' || shouldSkipByFrequency(popup)) {
    return false;
  }

  popup.dataset.popupOpened = '1';
  popup.__lastFocusedElement = document.activeElement;
  popup.classList.remove('hidden');
  popup.classList.add('is-open');
  popup.removeAttribute('aria-hidden');
  lockBodyScroll();
  updateCloseAvailability(popup);

  requestAnimationFrame(() => {
    const first = getFocusableElements(popup)[0] || popup;
    if (first && typeof first.focus === 'function') {
      first.focus({ preventScroll: true });
    }
  });

  return true;
}

function closePopup(popup) {
  if (!popup || !popup.classList.contains('is-open')) {
    return;
  }

  popup.classList.add('hidden');
  popup.classList.remove('is-open');
  popup.setAttribute('aria-hidden', 'true');

  const restore = popup.__lastFocusedElement;
  if (restore && typeof restore.focus === 'function' && document.contains(restore)) {
    restore.focus({ preventScroll: true });
  }

  unlockBodyScroll();
}

function bindPopup(popup) {
  if (!popup || popup.dataset.popupBound === '1') {
    return;
  }

  const closeButton = popup.querySelector('[data-popup-close]');
  const content = popup.querySelector('[data-popup-content]');

  if (closeButton) {
    closeButton.addEventListener('click', (event) => {
      event.preventDefault();
      if (closeButton.disabled) {
        return;
      }
      markAsSeen(popup);
      closePopup(popup);
    });
  }

  popup.querySelector('.carthago-popup__backdrop')?.addEventListener('click', (event) => {
    event.preventDefault();
    if (popup.dataset.popupRequireScroll !== '1' && popup.dataset.popupCloseOnBackdrop === '1') {
      markAsSeenOnStandardInteraction(popup);
      closePopup(popup);
    }
  });

  if (content) {
    content.addEventListener('scroll', () => updateCloseAvailability(popup), { passive: true });

    content.addEventListener('click', (event) => {
      const interactiveElement = event.target.closest('a[href], button');
      if (!interactiveElement || interactiveElement.hasAttribute('data-popup-close')) {
        return;
      }

      markAsSeenOnStandardInteraction(popup);
    });
  }

  popup.addEventListener('keydown', (event) => {
    if (!popup.classList.contains('is-open')) {
      return;
    }

    if (event.key === 'Escape') {
      if (popup.dataset.popupRequireScroll !== '1' && popup.dataset.popupCloseOnEscape === '1') {
        event.preventDefault();
        markAsSeenOnStandardInteraction(popup);
        closePopup(popup);
      }
      return;
    }

    if (event.key !== 'Tab') return;

    const focusables = getFocusableElements(popup);
    if (!focusables.length) {
      event.preventDefault();
      popup.focus({ preventScroll: true });
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === popup)) {
      event.preventDefault();
      last.focus({ preventScroll: true });
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  });

  popup.dataset.popupBound = '1';
}

export default function initPopups() {
  const body = document.body;
  if (
    body?.classList.contains('wp-admin') ||
    body?.classList.contains('block-editor-page') ||
    body?.classList.contains('block-editor-iframe__body')
  ) {
    return;
  }

  const popups = Array.from(document.querySelectorAll('[data-carthago-popup]'));
  if (!popups.length) {
    return;
  }

  popups.forEach(bindPopup);

  let hasOpened = false;
  popups.forEach((popup) => {
    if (hasOpened || shouldSkipByFrequency(popup)) {
      return;
    }

    const originalOpenPopup = openPopup;
    const tryOpen = () => {
      if (hasOpened) {
        return false;
      }
      const opened = originalOpenPopup(popup);
      hasOpened = opened || hasOpened;
      return opened;
    };

    popup.__tryOpen = tryOpen;

    const trigger = popup.dataset.popupTrigger || 'immediate';
    if (trigger === 'delay') {
      const delay = Math.max(0, parseInt(popup.dataset.popupDelay, 10) || 0);
      window.setTimeout(tryOpen, delay * 1000);
      return;
    }

    if (trigger === 'scroll') {
      const percent = Math.min(100, Math.max(1, parseInt(popup.dataset.popupScroll, 10) || 50));
      const onScroll = () => {
        const scrollable = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
        const current = Math.max(window.scrollY, document.documentElement.scrollTop || 0);

        if ((current / scrollable) * 100 >= percent) {
          window.removeEventListener('scroll', onScroll);
          tryOpen();
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      return;
    }

    tryOpen();
  });
}
